/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';

const filter = createFilterOptions();

function MPAutocomplete({
  editRowData,
  label,
  dropdownOptions = [],
  setFunction,
  field,
  setSourceSearch,
  isDisabled = false,
  selectedValue,
  setNewSource,
}) {
  const [selectedOption, setSelectedOption] = useState(
    selectedValue !== undefined ? selectedValue : (editRowData[field] || ''),
  );
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (selectedValue !== undefined) {
      setSelectedOption(selectedValue);
      setSearchValue(selectedValue);
    }
  }, [selectedValue]);

  useEffect(() => {
    if (setSourceSearch) {
      setSourceSearch(searchValue);
    }
  }, [searchValue]);

  // want to replace this with props validateFunction
  // but not sure how to check that a valid option is selected vs the default ('select one')
  const selectedOptionIsValid = (value) => value && dropdownOptions && dropdownOptions.length
    && dropdownOptions.indexOf(value) > -1;

  /* handle drop down change */
  // TODO refactor to make validation a prop (function passed in)
  function handleSelection(value) {
    // validation logic (in case triggered by input downKey event)
    let isValueValid = true;
    isValueValid = selectedOptionIsValid(value);
    if (!isValueValid) {
      Error('You need to select an option from the list.');
    }
  }

  return (
    <div>
      {label && (
      <InputLabel variant="standard" htmlFor="uncontrolled-native">
        {label}
        :
      </InputLabel>
      )}
      <Autocomplete
        disabled={isDisabled}
        freeSolo // helps avoid warnings since we are using an api call to populate options
        openOnFocus
        options={dropdownOptions}
        sx={{ width: '450px' }}
        value={selectedOption}
        onChange={(event, newValue) => {
          let newSource = false;
          if (setNewSource) setNewSource(false);
          event.stopPropagation();
          const delimiter = ' - ';
          const lastIndex = newValue.lastIndexOf(delimiter);
          let left = newValue.substring(0, lastIndex) || newValue;
          // if left starts with 'Add', we want to add the new value
          if (left.startsWith('Add ')) {
            if (field === 'code-source') {
              newSource = true;
              if (setNewSource) setNewSource(true);
            }
            // remove 'Add ' from the left part
            left = left.replace('Add ', '');
            // remove quotes if they exist
            left = left.replace(/['"]+/g, '');
          }
          // confirm that the selection is valid
          handleSelection(newValue);
          // display the left part (the actual value that gets saved to the DB)
          setSelectedOption(left);
          // for 'source' (modal/bulk-edit): pass the right part (channel_category) so
          // the setter can auto-populate channel_category from the selected source.
          // for 'code-source' (inline edit): pass the newSource boolean instead.
          const right = lastIndex !== -1 ? newValue.substring(lastIndex + delimiter.length) : null;
          const extra = field === 'code-source' ? newSource : right;
          setFunction(field, left, extra);
        }}
        inputValue={searchValue}
        onInputChange={(event, newInputValue) => {
          setSearchValue(newInputValue);
        }}
        onKeyDown={(event, newInputValue) => {
          if (event.key === 'Enter') {
            handleSelection(newInputValue);
          }
        }}
        renderOption={(props, option) => {
          // eslint-disable-next-line react/prop-types
          const { key } = props;
          const propCopy = { ...props };
          delete propCopy.key;
          const delimiter = ' - ';
          const lastIndex = option.lastIndexOf(delimiter);
          // If no delimiter is found, render the option as plain text
          if (lastIndex === -1) {
            // No delimiter, render as plain text
            return (
              <li key={key} {...propCopy}>
                <span style={{ display: 'inline' }}>{option}</span>
              </li>
            );
          }
          // Split the option into left and right parts
          const left = option.substring(0, lastIndex);
          const right = option.substring(lastIndex + delimiter.length);

          return (
            <li key={key} {...propCopy}>
              <span style={{ display: 'inline' }}>
                {left}
                {' - '}
                <strong>{right}</strong>
              </span>
            </li>
          );
        }}
        renderInput={(params) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <TextField {...params} />
        )}
        isOptionEqualToValue={(option, value) => value === '' || option === value}
        disableClearable
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          const { inputValue } = params;
          // Suggest the creation of a new value
          const isExisting = options.some(
            (option) => {
              const optionValue = typeof option === 'string' ? option : '';
              const input = typeof inputValue === 'string' ? inputValue : '';
              return optionValue.toLowerCase() === input.toLowerCase();
            },
          );
          // give user the option to add a new value only if this is not for sources
          if (inputValue !== '' && !isExisting && field !== 'source') {
            filtered.push(`Add "${inputValue}"`);
          }

          return filtered;
        }}
      />
    </div>
  );
}

export default MPAutocomplete;

MPAutocomplete.propTypes = {
  client: PropTypes.string,
  label: PropTypes.node,
  setFunction: PropTypes.func,
  editRowData: PropTypes.oneOfType([PropTypes.object]),
  dropdownOptions: PropTypes.oneOfType([PropTypes.array]),
  field: PropTypes.string,
  setSourceSearch: PropTypes.func,
  isDisabled: PropTypes.bool,
  selectedValue: PropTypes.string,
  setNewSource: PropTypes.func,
};
